/**
 * Interaction projection: the turns that stopped to ask a human.
 *
 * A question and its answer are two independent entries correlated by
 * `interactionId`, never one mutated entry. That is what makes the exchange
 * work across surfaces: the peer that asks and the peer that answers are
 * usually different machines, and both are appending to the same log
 * concurrently. Mutating the request in place would put two writers on one
 * entry; appending a second entry puts them on two, which is the shape a CRDT
 * merges without a conflict rule.
 *
 * Resolution is therefore a JOIN, and the FIRST answer wins. Two surfaces can
 * render the same pending question — that is the point of asking on the log —
 * so two people can answer it at once, and the causal order given by
 * `topoOrder` is the same on every peer. Last-write-wins would let a late
 * answer silently overwrite the one the agent already consumed and acted on.
 *
 * Peers without a handler for these events load them, preserve them, and simply
 * do not project them here.
 */

import { topoOrder } from './projection-order'
import type {
  InteractionKind,
  InteractionOutcome,
  LogDoc,
  TimestampMs,
} from './types'
import {
  INTERACTION_ANSWERED_EXTENSION,
  INTERACTION_REQUESTED_EXTENSION,
} from './types'

/** A pending interaction has no answer yet; the others carry one. */
export type InteractionStatus = 'pending' | InteractionOutcome

export interface InteractionAnswer {
  outcome: InteractionOutcome
  /** Kind-specific answer, still JSON-encoded. Empty when cancelled. */
  answerJson: string
  /** 'timeout' | 'dismissed' when cancelled. */
  reason?: string
  /** Who answered (= ProjectMember.userId). Absent when the waiter timed out. */
  by?: string
  at: TimestampMs
}

export interface Interaction {
  /** The entry id of the REQUEST. */
  id: string
  interactionId: string
  sessionId: string
  kind: InteractionKind
  title: string
  /** Kind-specific body, still JSON-encoded. Parsed by the renderer. */
  bodyJson: string
  at: TimestampMs
  status: InteractionStatus
  /** Present once resolved. */
  answer?: InteractionAnswer
}

const KINDS: ReadonlySet<string> = new Set(['question', 'tool_approval'])
const OUTCOMES: ReadonlySet<string> = new Set(['answered', 'cancelled'])

/** An on-doc scalar as a string, or undefined when absent/empty. */
function str(value: unknown): string | undefined {
  if (value == null) return undefined
  const text = String(value)
  return text.length > 0 ? text : undefined
}

/**
 * Every interaction on the log, in causal order, each joined to its answer.
 *
 * Answers are collected in a first pass so an answer that merges in BEFORE its
 * request (independent peers, no causal edge between the two entries) still
 * resolves rather than projecting as eternally pending.
 */
export function projectInteractions(doc: LogDoc): Interaction[] {
  const entries = topoOrder(doc)

  const answers = new Map<string, InteractionAnswer>()
  for (const e of entries) {
    const p = e.payload
    if (p.type !== 'extension') continue
    if (p.payload.extensionType !== INTERACTION_ANSWERED_EXTENSION) continue
    const raw = p.payload.payload
    const interactionId = str(raw.interactionId)
    const outcome = str(raw.outcome)
    if (!interactionId || !outcome || !OUTCOMES.has(outcome)) continue
    // First answer wins (see the header): a later one is a concurrent reply to
    // a question that was already resolved and consumed.
    if (answers.has(interactionId)) continue
    answers.set(interactionId, {
      outcome: outcome as InteractionOutcome,
      answerJson: raw.answerJson != null ? String(raw.answerJson) : '',
      ...(str(raw.reason) === undefined ? {} : { reason: str(raw.reason) }),
      ...(str(raw.by) === undefined ? {} : { by: str(raw.by) }),
      at: raw.at != null ? Number(raw.at) : e.createdAt,
    })
  }

  const out: Interaction[] = []
  const seen = new Set<string>()
  for (const e of entries) {
    const p = e.payload
    if (p.type !== 'extension') continue
    if (p.payload.extensionType !== INTERACTION_REQUESTED_EXTENSION) continue
    const raw = p.payload.payload
    const interactionId = str(raw.interactionId)
    const sessionId = str(raw.sessionId)
    const kind = str(raw.kind)
    const bodyJson = raw.bodyJson != null ? String(raw.bodyJson) : undefined
    if (!interactionId || !sessionId || !kind || bodyJson === undefined)
      continue
    if (!KINDS.has(kind)) continue
    // A duplicate request id is a bug upstream, not a second question; the
    // first one is the one the waiter is blocked on.
    if (seen.has(interactionId)) continue
    seen.add(interactionId)
    const answer = answers.get(interactionId)
    out.push({
      id: e.id,
      interactionId,
      sessionId,
      kind: kind as InteractionKind,
      title: str(raw.title) ?? '',
      bodyJson,
      at: raw.at != null ? Number(raw.at) : e.createdAt,
      status: answer === undefined ? 'pending' : answer.outcome,
      ...(answer === undefined ? {} : { answer }),
    })
  }
  return out
}

/**
 * The interactions still waiting on a human — what a surface renders.
 *
 * Ordered oldest-first by the projection, so a surface showing exactly one
 * prompt shows the one that has been blocking longest.
 */
export function pendingInteractions(doc: LogDoc): Interaction[] {
  return projectInteractions(doc).filter((i) => i.status === 'pending')
}

/** One interaction by its correlation id, answered or not. */
export function interactionById(
  doc: LogDoc,
  interactionId: string,
): Interaction | null {
  return (
    projectInteractions(doc).find((i) => i.interactionId === interactionId) ??
    null
  )
}
